import * as React from 'react';
import { formatAmount } from '@mlm/shared';
import { cn } from '../lib/cn';

/**
 * The single way to render a monetary value across the platform.
 *
 * Honors the Money-Has-Three-Signals Rule (DESIGN.md): every amount carries a
 * hue (credit / debit / neutral), a sign (+ / − / none), and optionally a word
 * (the ledger `kind`) so a reader can tell direction without parsing color.
 *
 * Always renders `tabular-nums` per the Tabular Money Rule. Never uses
 * `font-mono` — mono is too wide and crowds money columns.
 *
 * Direction:
 *  - 'credit'  → text-credit, leading '+' sign
 *  - 'debit'   → text-debit,  leading '−' sign (Unicode minus, not hyphen)
 *  - 'neutral' → text-foreground, no sign — for totals, balances, request rows
 */
export type MoneyDirection = 'credit' | 'debit' | 'neutral';

export type MoneyProps = {
  amount: string | number;
  currency?: string;
  direction: MoneyDirection;
  /** Optional ledger kind word, rendered after the amount as muted text. */
  kind?: string;
  /** Defaults true for credit/debit, false for neutral. */
  showSign?: boolean;
  /** Override decimal places. Default 2. */
  fractionDigits?: number;
  className?: string;
};

export function Money({
  amount,
  currency,
  direction,
  kind,
  showSign,
  fractionDigits = 2,
  className,
}: MoneyProps) {
  const effectiveShowSign = showSign ?? direction !== 'neutral';
  const formatted = formatAmount(amount, {
    minFractionDigits: fractionDigits,
    maxFractionDigits: fractionDigits,
  });
  const sign = !effectiveShowSign
    ? ''
    : direction === 'credit'
      ? '+'
      : direction === 'debit'
        ? '−'
        : '';
  const colorClass =
    direction === 'credit'
      ? 'text-credit'
      : direction === 'debit'
        ? 'text-debit'
        : 'text-foreground';

  return (
    <span className={cn('tabular-nums', colorClass, className)}>
      <span>
        {sign}
        {formatted}
      </span>
      {currency ? <span className="ml-1 text-muted-foreground">{currency}</span> : null}
      {kind ? <span className="ml-2 text-muted-foreground">· {kind}</span> : null}
    </span>
  );
}
